import { FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { isAuthError, useAuth } from '../../auth/AuthContext';
import { useLocale } from '../../i18n/LocaleContext';
import type { Locale, MessageKey } from '../../i18n/translations';
import { useStyles } from './LoginStyles';

const Login = () => {
  const classes = useStyles();
  const { locale, setLocale, t } = useLocale();
  const { user, login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<MessageKey | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    return <Navigate to="/schedule" replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login(username, password);
    } catch (cause) {
      setError(isAuthError(cause) ? (cause.code as MessageKey) : 'authError');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.page}>
      <Paper className={classes.card} elevation={1}>
        <div className={classes.header}>
          <div>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              {t('loginTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {t('loginSubtitle')}
            </Typography>
          </div>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={locale}
            onChange={(_, value: Locale | null) => {
              if (value) setLocale(value);
            }}
            aria-label={t('language')}
          >
            <ToggleButton value="en">EN</ToggleButton>
            <ToggleButton value="ro">RO</ToggleButton>
          </ToggleButtonGroup>
        </div>

        <form className={classes.form} onSubmit={onSubmit}>
          {error && <Alert severity="error">{t(error)}</Alert>}

          <TextField
            label={t('username')}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
            fullWidth
          />
          <TextField
            label={t('password')}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            fullWidth
          />

          <div className={classes.actions}>
            <Button type="submit" variant="contained" disabled={submitting} fullWidth>
              {t('signIn')}
            </Button>
          </div>
        </form>
      </Paper>
    </div>
  );
};

export default Login;
